import clsx from 'clsx'
import { forwardRef, type ButtonHTMLAttributes, type SVGProps, type ComponentType, type ReactNode } from 'react'

export type SelectButtonColor = 'neutral' | 'blue'
export type SelectButtonSize = 'xs' | 'sm'
/** Container widths the label can be hidden below. Add one here and to `hideLabelClasses` to offer it. */
export type SelectButtonHideLabelWidth = 400 | 480

export interface SelectButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual color variant. `neutral` for unselected; `blue` for active/selected. */
  color?: SelectButtonColor
  /** Optional size, defaults to `xs`. */
  size?: SelectButtonSize
  /** Optional leading icon rendered before the label. */
  leadingIcon?: ComponentType<SVGProps<SVGSVGElement>>
  /** Optional trailing icon rendered after the label (e.g. ChevronDown for dropdowns). */
  trailingIcon?: ComponentType<SVGProps<SVGSVGElement>>
  /** The label text to display. */
  children?: ReactNode
  /** Hide the label once the button's container is narrower than this. Requires an ancestor with container-type: inline-size. */
  hideLabelBelow?: SelectButtonHideLabelWidth
}

/* Literal classes so Tailwind's scanner compiles them. */
const hideLabelClasses: Record<SelectButtonHideLabelWidth, string> = {
  400: '[@container(max-width:400px)]:hidden',
  480: '[@container(max-width:480px)]:hidden',
}

const colorStyles: Record<SelectButtonColor, string> = {
  neutral: clsx('border-border-strong bg-surface-field text-text-tertiary', 'hocus:bg-surface-hover', 'disabled:opacity-50 disabled:cursor-not-allowed'),
  blue: clsx(
    'border-brand-200 bg-surface-brand-selected-subtle text-brand-500',
    'hocus:bg-surface-brand-hover-strong hocus:border-brand-300',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  ),
}

const sizeStyles: Record<SelectButtonSize, string> = {
  xs: 'h-6 gap-1 rounded px-2 text-xs leading-4',
  sm: 'h-8 gap-1.5 rounded-md px-2.5 py-1.5 text-sm leading-5 tracking-[-0.15px]',
}

const iconSizeStyles: Record<SelectButtonSize, string> = {
  xs: 'h-3.5 w-3.5',
  sm: 'h-5 w-5',
}

export const SelectButton = forwardRef<HTMLButtonElement, SelectButtonProps>(function SelectButton(
  { color = 'neutral', size = 'xs', leadingIcon: LeadingIcon, trailingIcon: TrailingIcon, children, className, hideLabelBelow, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      data-ds-component="SelectButton"
      className={clsx(
        'inline-flex items-center overflow-hidden border font-medium transition-all duration-300',
        sizeStyles[size],
        'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset',
        colorStyles[color],
        className
      )}
      {...rest}
    >
      {LeadingIcon && <LeadingIcon className={clsx(iconSizeStyles[size], 'shrink-0')} aria-hidden />}
      {children && <span className={clsx('whitespace-nowrap', hideLabelBelow && hideLabelClasses[hideLabelBelow])}>{children}</span>}
      {TrailingIcon && <TrailingIcon className={clsx(iconSizeStyles[size], 'shrink-0')} aria-hidden />}
    </button>
  )
})
